import type { Copy } from '@/i18n/en'
import type { LocaleCode } from '@/i18n/locales'
import { LOCALE_CODES } from '@/i18n/locales'
import { format } from '@/i18n/format'
import type { Profile } from '@/profile/types'
import { extractAffirmation, extractLanguage, extractName } from '@/voice/nlu/extract'
import { normalize } from '@/voice/nlu/normalize'

/**
 * What Namu says back in an open conversation.
 *
 * This is the seam where the speech models will plug in. Right now there is
 * exactly one implementation and it runs entirely on the device: it handles
 * the things the frontend genuinely can do, and for everything else it says
 * plainly that the model is not connected yet.
 *
 * That honesty is a design decision, not a placeholder. A voice assistant that
 * improvises a confident-sounding answer it cannot support is worse than one
 * that admits its limits, and the whole premise of this product is being
 * trusted to understand people in their own language.
 *
 * When the backend lands, it implements `ReplyProvider` and nothing above this
 * file changes.
 */

export type ReplyIntent =
  | 'answer'
  | 'profile-read'
  | 'profile-update'
  | 'language-change'
  | 'help'
  | 'end'
  | 'unsupported'

export interface ReplyInput {
  text: string
  profile: Profile
  locale: LocaleCode
  copy: Copy
}

export interface Reply {
  text: string
  intent: ReplyIntent
  /** A change the caller should apply to the stored profile. */
  patch?: { name?: string; locale?: LocaleCode }
}

export interface ReplyProvider {
  readonly id: string
  /** Whether replies come from a real language model. Shown in diagnostics. */
  readonly modelBacked: boolean
  respond(input: ReplyInput): Promise<Reply>
}

/** Phrases that mean "tell me what you have on me". */
const PROFILE_QUERIES: Record<LocaleCode, readonly string[]> = {
  en: [
    'what do you know about me',
    'what do you remember',
    'what have you saved',
    'my profile',
    'what did i tell you',
  ],
  ha: ['me ka sani game da ni', 'me ka tuna', 'me ka adana', 'bayanina'],
}

const HELP_QUERIES: Record<LocaleCode, readonly string[]> = {
  en: ['what can you do', 'help', 'what should i say', 'how does this work'],
  ha: ['me za ka iya yi', 'taimako', 'me zan ce', 'yaya wannan ke aiki'],
}

const GOODBYES: Record<LocaleCode, readonly string[]> = {
  en: ['goodbye', 'bye', 'stop', 'we are done', 'that is all', 'thank you goodbye'],
  ha: ['sai an jima', 'sai wani lokaci', 'mun gama', 'tsaya', 'shi ke nan'],
}

const RENAME_CUES: Record<LocaleCode, readonly string[]> = {
  en: ['call me', 'my name is', 'change my name', 'i am called'],
  ha: ['ka kira ni', 'sunana', 'canza sunana'],
}

const LANGUAGE_CUES: Record<LocaleCode, readonly string[]> = {
  en: ['speak', 'switch to', 'change the language', 'in hausa', 'in english'],
  ha: ['yi magana da', 'canza yare', 'da hausa', 'da turanci'],
}

function matchesAny(text: string, phrases: readonly string[]): boolean {
  return phrases.some((phrase) => text.includes(normalize(phrase)))
}

function describeProfile(profile: Profile, copy: Copy): string {
  const parts: string[] = []
  if (profile.name) parts.push(`${copy.ui.field.name}, ${profile.name.value}`)
  if (profile.region) parts.push(`${copy.ui.field.region}, ${profile.region.value}`)
  if (profile.ageBand) {
    parts.push(`${copy.ui.field.ageBand}, ${copy.ui.ageBand[profile.ageBand.value]}`)
  }
  if (profile.gender) {
    parts.push(`${copy.ui.field.gender}, ${copy.ui.gender[profile.gender.value]}`)
  }
  if (profile.interests) {
    parts.push(`${copy.ui.field.interests}, ${profile.interests.value.join(', ')}`)
  }
  return parts.join('. ')
}

/**
 * The on-device provider.
 *
 * Deliberately narrow. It covers the handful of intents that are genuinely
 * local, and does not attempt anything else.
 */
export class LocalReplyProvider implements ReplyProvider {
  readonly id = 'local'
  readonly modelBacked = false

  async respond({ text, profile, locale, copy }: ReplyInput): Promise<Reply> {
    const normalized = normalize(text)

    if (normalized.length === 0) {
      return { text: copy.speech.didNotCatch[0] as string, intent: 'unsupported' }
    }

    // Checked before the affirmation tables, because a language request can
    // contain words that read as yes in the other language. "Switch to
    // English" carries "to", which is a Hausa yes.
    const languageRequest = LOCALE_CODES.flatMap((code) => LANGUAGE_CUES[code])
    const wantsLanguage = matchesAny(normalized, languageRequest)

    if (!wantsLanguage && matchesAny(normalized, GOODBYES[locale])) {
      return { text: copy.speech.goodbye[0] as string, intent: 'end' }
    }

    if (!wantsLanguage && matchesAny(normalized, HELP_QUERIES[locale])) {
      return { text: copy.speech.helpOffer[0] as string, intent: 'help' }
    }

    if (matchesAny(normalized, PROFILE_QUERIES[locale])) {
      const described = describeProfile(profile, copy)
      if (described.length === 0) {
        return { text: copy.speech.profileEmpty[0] as string, intent: 'profile-read' }
      }
      return {
        text: `${copy.speech.profileReadIntro[0] as string} ${described}.`,
        intent: 'profile-read',
      }
    }

    // A language switch has to be understood in EVERY language, since the
    // person asking is by definition not being served well by the current one.
    // Gating on the active locale meant you already had to speak English to
    // ask for Hausa, which is precisely backwards.
    if (wantsLanguage) {
      const requested = extractLanguage(text)
      if (requested.value && requested.value !== locale) {
        return {
          // Spoken in the language being switched to, which is the only way it
          // is any use to the person who asked.
          text: '',
          intent: 'language-change',
          patch: { locale: requested.value },
        }
      }
    }

    if (matchesAny(normalized, RENAME_CUES[locale])) {
      const name = extractName(text, locale)
      if (name.value) {
        return {
          text: format(copy.speech.nameUpdated[0] as string, { name: name.value }),
          intent: 'profile-update',
          patch: { name: name.value },
        }
      }
    }

    // A bare yes or no with nothing to answer is a dangling turn, not a
    // request. Offering the menu is more useful than a canned apology.
    if (extractAffirmation(text, locale).value !== null) {
      return { text: copy.speech.helpOffer[0] as string, intent: 'help' }
    }

    return { text: copy.speech.modelNotConnected[0] as string, intent: 'unsupported' }
  }
}
